import React, { useEffect, useState } from 'react';
import { CheckCircle, TrendingUp, TrendingDown, DollarSign, X } from 'lucide-react';

/**
 * FinancialNotification - Shows financial impact notifications after actions
 * Displays revenue/cost impact in a user-friendly way
 */
const FinancialNotification = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      setIsLeaving(false);

      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300); // Wait for animation to complete
  };

  if (!notification || !isVisible) return null;

  const getIcon = () => {
    switch (notification.type) {
      case 'load_created':
      case 'load_delivered':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'expense_created':
      case 'payment_created':
        return <TrendingDown className="w-5 h-5 text-orange-600" />;
      default:
        return <DollarSign className="w-5 h-5 text-blue-600" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'load_created':
      case 'load_delivered':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200';
      case 'expense_created':
      case 'payment_created':
        return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
      default:
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200';
    }
  };

  const formatImpact = () => {
    if (!notification.financialImpact) return null;

    const { revenue, expense, netImpact } = notification.financialImpact;

    return (
      <div className="mt-2 pt-2 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-2 text-sm">
          {revenue !== undefined && revenue !== 0 && (
            <div>
              <span className="text-gray-600">Revenue:</span>
              <span className="ml-1 font-semibold text-green-700">
                +${revenue.toLocaleString()}
              </span>
            </div>
          )}
          {expense !== undefined && expense !== 0 && (
            <div>
              <span className="text-gray-600">Expense:</span>
              <span className="ml-1 font-semibold text-red-700">
                -${Math.abs(expense).toLocaleString()}
              </span>
            </div>
          )}
          {netImpact !== undefined && (
            <div>
              <span className="text-gray-600">Net:</span>
              <span className={`ml-1 font-semibold ${netImpact >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {netImpact >= 0 ? '+' : '-'}${Math.abs(netImpact).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isLeaving ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      }`}
    >
      <div className={`rounded-lg shadow-lg border p-4 min-w-[320px] max-w-md ${getBackgroundColor()}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="font-semibold text-gray-900">{notification.title || 'Success'}</p>
              </div>
              <p className="mt-1 text-sm text-gray-700">{notification.message}</p>
              {formatImpact()}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialNotification;